// OrderProgressService.ts
import { supabase } from '../lib/supabase/client/supabaseClient';

export interface OrderProgressData {
  id: string;
  user_id: string;
  order_id?: string;
  payment_id?: string;
  status: OrderProgressStatus;
  progress_data: Record<string, any>;
  error_message?: string;
  error_step?: string;
  created_at: string;
  updated_at: string;
}

export type OrderProgressStatus =
  | 'order_created'
  | 'document_uploading'
  | 'document_uploaded'
  | 'payment_pending'
  | 'payment_initiated'
  | 'payment_completed'
  | 'processing'
  | 'completed'
  | 'failed';

class OrderProgressService {
  // Create a new order progress record
  async createOrderProgress(progressData: Record<string, any> = {}): Promise<string> {
    try {
      const { data, error } = await supabase
        .rpc('create_order_progress', {
          progress_data_param: progressData
        });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating order progress:', error);
      throw error;
    }
  }

  // Update order progress status and data
  async updateOrderProgress(
    progressId: string,
    updates: {
      status?: OrderProgressStatus;
      progressData?: Record<string, any>;
      orderId?: string;
      paymentId?: string;
      errorMessage?: string;
      errorStep?: string;
    }
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .rpc('update_order_progress', {
          progress_id: progressId,
          new_status: updates.status,
          progress_data_param: updates.progressData,
          order_id_param: updates.orderId,
          payment_id_param: updates.paymentId,
          error_message_param: updates.errorMessage,
          error_step_param: updates.errorStep
        });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating order progress:', error);
      throw error;
    }
  }

  // Get a specific order progress record
  async getOrderProgress(progressId: string): Promise<OrderProgressData | null> {
    try {
      const { data, error } = await supabase
        .rpc('get_order_progress', {
          progress_id: progressId
        });

      if (error) throw error;
      return data?.[0] || null;
    } catch (error) {
      console.error('Error getting order progress:', error);
      throw error;
    }
  }

  // Get all order progress records for current user
  async getUserOrderProgress(): Promise<OrderProgressData[]> {
    try {
      const { data, error } = await supabase
        .rpc('get_user_order_progress');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting user order progress:', error);
      throw error;
    }
  }

  // Delete an order progress record
  async deleteOrderProgress(progressId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .rpc('delete_order_progress', {
          progress_id: progressId
        });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error deleting order progress:', error);
      throw error;
    }
  }

  // Helper methods for common status updates
  async markDocumentUploading(progressId: string): Promise<void> {
    await this.updateOrderProgress(progressId, { status: 'document_uploading' });
  }

  async markDocumentUploaded(progressId: string, fileData: Record<string, any>): Promise<void> {
    await this.updateOrderProgress(progressId, {
      status: 'document_uploaded',
      progressData: fileData
    });
  }

  async markPaymentPending(progressId: string): Promise<void> {
    await this.updateOrderProgress(progressId, { status: 'payment_pending' });
  }

  async markPaymentInitiated(progressId: string, orderId: string): Promise<void> {
    await this.updateOrderProgress(progressId, {
      status: 'payment_initiated',
      orderId
    });
  }

  async markPaymentCompleted(progressId: string, paymentId: string): Promise<void> {
    await this.updateOrderProgress(progressId, {
      status: 'payment_completed',
      paymentId
    });
  }

  async markProcessing(progressId: string): Promise<void> {
    await this.updateOrderProgress(progressId, { status: 'processing' });
  }

  async markCompleted(progressId: string): Promise<void> {
    await this.updateOrderProgress(progressId, { status: 'completed' });
  }

  async markFailed(progressId: string, errorMessage: string, errorStep?: string): Promise<void> {
    await this.updateOrderProgress(progressId, {
      status: 'failed',
      errorMessage,
      errorStep
    });
  }
}

export const orderProgressService = new OrderProgressService();
export default orderProgressService;